/**
 * CopilotPanel — the persistent copilot surface: a floating launcher plus a
 * slide-in chat panel available on both the startup screen and inside a project.
 * Shows the conversation, a live "what the agent is doing" activity log while a
 * run is in flight, an Undo button after edits, and an input box.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useCopilot } from './CopilotProvider';
import { useWalkthrough } from '../Walkthrough/WalkthroughProvider';
import { useProjectStore } from '../../store/projectStore';
import { computeNextStep } from '../Workspace/NextStepCard';
import './CopilotPanel.css';
import { getActiveImpactSource, hasImpactScores, GREET_SOURCE } from '../../services/impactSource';

// Human-readable label for an agent step shown in the live activity log.
function stepLabel(step) {
  if (step.type === 'action') {
    const a = step.name;
    const args = step.args || {};
    switch (a) {
      case 'get_project_summary': return 'Reading the project…';
      case 'get_process': return `Looking at ${args.processId || 'a process'}…`;
      case 'get_analysis_results': return 'Reading analysis results…';
      case 'search_lci_database': return `Searching the LCI database for "${args.query}"…`;
      case 'search_cost_database': return `Searching costs for "${args.query}"…`;
      case 'list_templates': return 'Listing templates…';
      case 'create_project_from_template': return 'Creating a project from a template…';
      case 'create_project_from_description': return 'Generating a template from your description…';
      case 'add_process': return `Adding process "${args.name || ''}"…`;
      case 'update_process': return `Updating ${args.processId}…`;
      case 'rename_process': return `Renaming ${args.processId}…`;
      case 'delete_process': return `Deleting ${args.processId}…`;
      case 'add_lci_input': return `Adding "${args.name}" to ${args.processId}…`;
      case 'update_lci_input': return `Updating an input in ${args.processId}…`;
      case 'remove_lci_input': return `Removing an input from ${args.processId}…`;
      case 'set_input_link': return `Linking an input in ${args.processId} to the database…`;
      case 'set_output_price': return `Setting an output price in ${args.processId}…`;
      case 'set_scaling_factor': return `Setting scaling factor to ${args.value}…`;
      case 'set_project_field': return `Updating ${args.field}…`;
      case 'run_lca': return 'Running LCA…';
      case 'run_tea': return 'Running TEA…';
      case 'run_circularity': return 'Running circularity…';
      case 'run_all': return 'Running all analyses…';
      case 'save_project': return 'Saving…';
      case 'export_results': return 'Exporting results…';
      case 'navigate': return 'Switching the view…';
      case 'highlight': return 'Pointing something out…';
      default: return `${a}…`;
    }
  }
  return null;
}

// Count inventory inputs across the project that aren't linked to the DB.
function countUnlinkedInputs(project) {
  let n = 0;
  const source = getActiveImpactSource(project);
  Object.values(project?.processes || {}).forEach((p) => {
    (p.lci_inputs || []).forEach((inp) => {
      if (!hasImpactScores(inp, source)) n += 1;
    });
  });
  return n;
}

/**
 * Context-aware quick actions. Each chip is { label (short), prompt (the full
 * natural-language request the agent runs with its tools) }. The set is tailored
 * to *where the user is* — screen (startup) and workspace mode — plus a little
 * project state, so the panel never shows the same generic list everywhere.
 */
function buildSuggestions({ screen, mode, selectedName, hasResults, dirty, unlinked, processCount, impactSource, citedShare }) {
  if (screen === 'startup') {
    return [
      { label: 'Help me get started', prompt: 'Help me get started — walk me through my options.' },
      { label: 'Design a cell with BatPaC', prompt: 'I want to design a cell with BatPaC. Ask me for the cathode and any numbers I have for it, then compare the designs.' },
      { label: 'Build from a description', prompt: 'I want to build a template from a description of my process. Ask me what you need to know.' },
      { label: 'Start from a sample', prompt: 'Start me off from one of the samples.' },
    ];
  }

  if (mode === 'inventory') {
    const out = [];
    if (unlinked > 0) {
      out.push({ label: `Link ${unlinked} unlinked input${unlinked === 1 ? '' : 's'}`, prompt: 'Link my unlinked inventory inputs to the best-matching database activities so LCA can run.' });
    }
    out.push({ label: 'Look up a live price', prompt: 'Find a current market price for one of my inventory inputs and tell me the source. Ask me which input if you need to.' });
    out.push({ label: 'Review my inventory', prompt: 'Review my project inventory and tell me what looks missing, unlinked, or unpriced.' });
    out.push({ label: 'How is inventory used?', prompt: 'Explain what the inventory is and how it feeds into the LCA and TEA.' });
    return out;
  }

  if (mode === 'results' || mode === 'compare') {
    const out = [];
    if (!hasResults) out.push({ label: 'Run the full analysis', prompt: 'Run the full analysis (LCA, TEA, and circularity).' });
    else if (dirty) out.push({ label: 'Re-run the analysis', prompt: 'The project changed — re-run the full analysis and tell me what moved.' });
    if (hasResults) {
      out.push({ label: 'Biggest cost driver?', prompt: 'What is the biggest cost driver in my project, and by how much?' });
      out.push({ label: 'Summarize the results', prompt: 'Summarize my LCA and TEA results in plain language.' });
      if (impactSource === GREET_SOURCE) {
        out.push({ label: 'What does GREET miss?', prompt: 'Which of my inputs does GREET 2025 not cover, and how does that affect the totals?' });
      } else {
        out.push({ label: 'Compare with GREET data', prompt: 'Re-run the LCA on GREET 2025 open data, tell me how the climate-change total compares with ecoinvent and what GREET does not cover, then switch back.' });
      }
      if (citedShare != null && citedShare < 0.5) {
        out.push({ label: 'Source the biggest prices', prompt: 'Most of my cost total is uncited. Find a current market price, with its source, for my biggest cost driver.' });
      }
      out.push({ label: 'Export the results', prompt: 'Export my results to a spreadsheet.' });
    }
    out.push({ label: 'How does the analysis work?', prompt: 'Explain how the LCA and TEA are calculated here.' });
    return out;
  }

  // default: Build mode
  if (processCount === 0) {
    return [{ label: 'Add the first process', prompt: 'This project has no processes yet — help me add the first one.' }];
  }
  const out = [];
  if (selectedName) {
    const clean = String(selectedName).replace(/_/g, ' ');
    out.push({ label: `Add an input to ${clean}`, prompt: `Add an inventory input to the "${selectedName}" process. Ask me for the material, quantity, and unit if you need them.` });
    out.push({ label: `Edit ${clean}`, prompt: `Help me edit the "${selectedName}" process.` });
    out.push({ label: `Explain ${clean}`, prompt: `Explain what the "${selectedName}" process does and what its inputs are.` });
  } else {
    out.push({ label: 'Explain this process flow', prompt: 'Explain what this template models and how the processes connect.' });
  }
  out.push({ label: 'Add a new process', prompt: 'Add a new process to my project. Ask me where it goes and what it is.' });
  out.push({ label: 'How do I build a template?', prompt: 'Walk me through how to build and edit a template in LiTeCAB.' });
  return out;
}

const BOT_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2a2 2 0 0 1 2 2v1h3a2 2 0 0 1 2 2v3h1a2 2 0 0 1 0 4h-1v3a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-3H4a2 2 0 0 1 0-4h1V7a2 2 0 0 1 2-2h3V4a2 2 0 0 1 2-2z" />
    <circle cx="9" cy="11" r="1" fill="currentColor" />
    <circle cx="15" cy="11" r="1" fill="currentColor" />
  </svg>
);

const PLAY_ICON = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="6 4 20 12 6 20 6 4" fill="currentColor" stroke="none" />
  </svg>
);

export default function CopilotPanel() {
  const { messages, activity, isRunning, open, setOpen, docked, sendMessage, undo, undoable, clearChat } = useCopilot();
  const { firstRunHint, start: startWalkthrough, dismissHint } = useWalkthrough();
  const [input, setInput] = useState('');
  const endRef = useRef(null);
  const taRef = useRef(null);

  // "What's next" guidance — moved here from the old top-of-workspace banner so
  // all assistant communication lives in this one panel. Reads the store directly
  // (the panel is mounted at the app root, outside WorkspaceContext).
  const project = useProjectStore((s) => s.project);
  const analysisResults = useProjectStore((s) => s.analysisResults);
  const dirty = useProjectStore((s) => s.dirty);
  const workspaceMode = useProjectStore((s) => s.workspaceMode);
  const selectedProcess = useProjectStore((s) => s.selectedProcess);
  const showStartup = useProjectStore((s) => s.showStartup);
  const goToMode = useProjectStore((s) => s.goToMode);
  const runAll = useProjectStore((s) => s.runAll);
  const addProcess = useProjectStore((s) => s.addProcess);
  const [nudgeDismissed, setNudgeDismissed] = useState(null);

  const nextStep = computeNextStep(project, analysisResults, dirty);
  const showNudge = !!nextStep && !isRunning && nudgeDismissed !== nextStep.id;

  // Quick actions tailored to where the user is right now (see buildSuggestions).
  const { lca, tea, circularity } = analysisResults || {};
  const suggestions = buildSuggestions({
    screen: showStartup || !project ? 'startup' : 'project',
    mode: workspaceMode,
    selectedName: selectedProcess?.name || null,
    hasResults: !!(lca?.success || tea?.success || circularity?.success),
    dirty,
    unlinked: countUnlinkedInputs(project),
    impactSource: getActiveImpactSource(project),
    citedShare: analysisResults?.tea?.cost_provenance?.cited_share ?? null,
    processCount: Object.keys(project?.processes || {}).length,
  });

  const runNudge = () => {
    if (!nextStep) return;
    switch (nextStep.actionKind) {
      case 'run': runAll(); goToMode('results'); break;
      case 'link': goToMode('results'); break;
      case 'add': goToMode('build'); addProcess(); break;
      case 'build': default: goToMode('build'); break;
    }
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activity, isRunning]);

  useEffect(() => {
    if (taRef.current) {
      taRef.current.style.height = 'auto';
      taRef.current.style.height = Math.min(taRef.current.scrollHeight, 120) + 'px';
    }
  }, [input]);

  const submit = (text) => {
    const t = (text ?? input).trim();
    if (!t || isRunning) return;
    sendMessage(t);
    setInput('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  const liveSteps = activity.map(stepLabel).filter(Boolean);

  const launchTour = () => { setOpen(false); startWalkthrough(); };

  return (
    <>
      {/* Inside a project the collapsed copilot is a slim rail in its own
          reserved column (App adds .app--dock-rail), so it covers nothing. */}
      {!open && docked && (
        <nav className="copilot-rail" aria-label="Copilot">
          <button className="copilot-rail-btn" onClick={() => setOpen(true)} title="Open the LiTeCAB Copilot">
            {BOT_ICON}
            <span>Copilot</span>
          </button>
          <button className="copilot-rail-btn" onClick={launchTour} title="Take the guided tour">
            {PLAY_ICON}
            <span>Tour</span>
          </button>
        </nav>
      )}

      {!open && !docked && (
        <div className="copilot-fab-wrap">
          {firstRunHint && (
            <div className="copilot-fab-hint">
              <span>👋 New here? Take a quick tour</span>
              <button className="copilot-fab-hint-x" onClick={dismissHint} title="Dismiss">×</button>
            </div>
          )}
          <button
            className={`copilot-fab${firstRunHint ? ' copilot-fab--pulse' : ''}`}
            onClick={() => setOpen(true)}
            title="Open the LiTeCAB Copilot"
          >
            {BOT_ICON}
            <span>Copilot</span>
          </button>
          {/* Walkthrough sits directly under the launcher so it's the first
              thing a new user sees on entry, on every screen. */}
          <button className="copilot-fab-tour" onClick={launchTour} title="Take the guided tour">
            {PLAY_ICON}
            <span>Take a tour</span>
          </button>
        </div>
      )}

      {open && (
        <div className={`copilot-panel${docked ? ' copilot-panel--docked' : ''}`} data-coach="copilot-panel">
          <div className="copilot-header">
            <div className="copilot-title">
              <span className="copilot-dot" /> LiTeCAB Copilot
            </div>
            <div className="copilot-header-actions">
              <button onClick={launchTour} title="Take the guided tour" className="copilot-header-tour">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><polygon points="6 4 20 12 6 20 6 4" /></svg>
                Tour
              </button>
              <button onClick={clearChat} title="Clear conversation" className="copilot-icon-btn">⟳</button>
              <button onClick={() => setOpen(false)} title={docked ? 'Collapse' : 'Close'} className="copilot-icon-btn">×</button>
            </div>
          </div>

          <div className="copilot-messages">
            {messages.length === 0 && !isRunning && (
              <div className="copilot-welcome">
                <h3>How can I help?</h3>
                <p>I can help you get started, build and edit templates, run analysis, and explain the results.</p>
                <button onClick={launchTour} className="copilot-tour-btn">
                  ▶ Take the guided tour
                </button>
                <div className="copilot-suggestions">
                  {suggestions.map((s) => (
                    <button key={s.label} onClick={() => submit(s.prompt)} className="copilot-suggestion" title={s.prompt}>{s.label}</button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`copilot-msg ${m.role}`}>{m.text}</div>
            ))}

            {isRunning && (
              <div className="copilot-activity">
                {liveSteps.length > 0 ? (
                  liveSteps.map((s, i) => (
                    <div key={i} className={`copilot-step ${i === liveSteps.length - 1 ? 'current' : 'done'}`}>
                      {i === liveSteps.length - 1
                        ? <span className="copilot-spinner" />
                        : <span className="copilot-check">✓</span>}
                      {s}
                    </div>
                  ))
                ) : (
                  <div className="copilot-step current"><span className="copilot-spinner" /> Thinking…</div>
                )}
              </div>
            )}

            <div ref={endRef} />
          </div>

          {undoable && !isRunning && (
            <div className="copilot-undo-bar">
              <span>I made changes to your project.</span>
              <button onClick={undo}>Undo last change</button>
            </div>
          )}

          {showNudge && (
            <div className="copilot-nudge">
              <span className="copilot-nudge-dot" />
              <span className="copilot-nudge-msg">{nextStep.message}</span>
              <button className="copilot-nudge-act" onClick={runNudge}>{nextStep.actionLabel}</button>
              <button className="copilot-nudge-x" title="Dismiss" onClick={() => setNudgeDismissed(nextStep.id)}>×</button>
            </div>
          )}

          {/* Context-aware quick actions — always available (not just the empty
              welcome), so the panel reflects where the user is. */}
          {messages.length > 0 && !isRunning && suggestions.length > 0 && (
            <div className="copilot-quickbar" aria-label="Suggested actions">
              {suggestions.slice(0, 4).map((s) => (
                <button key={s.label} className="copilot-quickchip" onClick={() => submit(s.prompt)} title={s.prompt}>
                  {s.label}
                </button>
              ))}
            </div>
          )}

          <div className="copilot-input-row">
            <textarea
              ref={taRef}
              className="copilot-textarea"
              placeholder={isRunning ? 'Working…' : 'Ask the copilot…'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              disabled={isRunning}
            />
            <button className="copilot-send" onClick={() => submit()} disabled={isRunning || !input.trim()}>
              {isRunning ? '…' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
